import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import BrandMark from '../../BrandMark/BrandMark';
import Icon from '../../ui/Icon/Icon';
import PillLink from '../../ui/PillLink/PillLink';
import { CONTACT_EMAIL, CONTACT_PATH, primaryNavigation } from '../../../data/navigation';
import { SUPPORTED_LANGUAGES } from '../../../i18n/config';
import { useLanguage } from '../../../i18n/useLanguage';
import styles from './MobileMenu.module.css';

const PANEL_ID = 'mobile-menu';
const EASE = [0.16, 1, 0.3, 1] as const;

function useMenuLifecycle(isOpen: boolean, close: () => void, closeButton: RefObject<HTMLButtonElement | null>, toggleButton: RefObject<HTMLButtonElement | null>) {
    useEffect(() => {
        if (!isOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeButton.current?.focus();
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };
        window.addEventListener('keydown', closeOnEscape);
        const toggle = toggleButton.current;

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', closeOnEscape);
            toggle?.focus();
        };
    }, [isOpen, close, closeButton, toggleButton]);
}

export default function MobileMenu() {
    const { t } = useTranslation();
    const { language, setLanguage, localizedPath } = useLanguage();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [canPortal, setCanPortal] = useState(false);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => setCanPortal(true), []);
    useEffect(() => setIsOpen(false), [location.key]);
    useMenuLifecycle(isOpen, close, closeRef, toggleRef);

    const panel = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    id={PANEL_ID}
                    className={styles.panel}
                    data-theme="dark"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('nav.menu')}
                    initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                    animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                    exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    <div className={styles.top}>
                        <Link to={localizedPath('/')} className={styles.home} aria-label={t('a11y.homeLink')}>
                            <BrandMark className={styles.mark} />
                        </Link>
                        <button ref={closeRef} type="button" className={styles.toggle} onClick={close}>
                            {t('nav.close')}
                            <Icon name="close" className={styles.toggleIcon} />
                        </button>
                    </div>

                    <nav aria-label={t('a11y.primaryNav')}>
                        <ul className={styles.links}>
                            {primaryNavigation.map((item, index) => (
                                <li key={item.path} className={styles.linkRow}>
                                    <motion.span
                                        className={styles.linkReveal}
                                        initial={{ y: '110%' }}
                                        animate={{ y: '0%' }}
                                        transition={{ duration: 0.8, ease: EASE, delay: 0.18 + index * 0.06 }}
                                    >
                                        <Link to={localizedPath(item.path)} className={styles.link}>
                                            {t(item.labelKey)}
                                            <Icon name="arrowUpRight" className={styles.linkIcon} />
                                        </Link>
                                    </motion.span>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <motion.div
                        className={styles.bottom}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: EASE, delay: 0.45 }}
                    >
                        <div className={styles.languages} role="group" aria-label={t('language.label')}>
                            {SUPPORTED_LANGUAGES.map((code) => (
                                <button
                                    key={code}
                                    type="button"
                                    lang={code}
                                    className={styles.language}
                                    aria-pressed={code === language}
                                    onClick={() => setLanguage(code)}
                                >
                                    {code.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <PillLink href={localizedPath(CONTACT_PATH)} size="large" icon="arrowRight" className={styles.cta}>
                            {t('nav.cta')}
                        </PillLink>
                        <a className={styles.email} href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            <button
                ref={toggleRef}
                type="button"
                className={`${styles.toggle} ${styles.opener}`}
                aria-expanded={isOpen}
                aria-controls={PANEL_ID}
                onClick={() => setIsOpen(true)}
            >
                {t('nav.menu')}
                <Icon name="menu" className={styles.toggleIcon} />
            </button>
            {canPortal && createPortal(panel, document.body)}
        </>
    );
}
